import graphql__45313cf56d4fd7a56a04 from "./__generated__/AnalysisExpenseOverviewViewQuery.graphql.ts";import * as React from "react";
import {useTransition} from "react";
import {graphql} from "react-relay";
import {useLazyLoadQuery} from "react-relay/hooks";
import {useIntl} from "react-intl";
import Panel from "./presentational/Panel";
import Hint from "./presentational/Hint";
import KpiCard from "./presentational/KpiCard";
import PeriodTable, {PeriodColumn, PeriodRow} from "./presentational/table/PeriodTable";
import StackedBarChart from "./presentational/chart/StackedBarChart";
import MonthRangeHeader from "./presentational/analysis/MonthRangeHeader";
import MonthKpiCards from "./presentational/analysis/MonthKpiCards";
import RankingList, {RankingEntry} from "./presentational/analysis/RankingList";
import PeriodTableLegend from "./presentational/analysis/PeriodTableLegend";
import {formatWholeAmount} from "../helper/revenueFormat";
import {ExpenseCategory} from "../helper/expenseLabels";
import {expenseInvoicesPath, NO_VALUE} from "../helper/expenseInvoiceFilter";
import {monthToDate, useAnalysisMonths} from "../helper/analysisMonths";
import type {AnalysisExpenseOverviewViewQuery as AnalysisExpenseOverviewViewQueryType} from "./__generated__/AnalysisExpenseOverviewViewQuery.graphql";

const UNASSIGNED_ROW_ID = NO_VALUE;

/**
 * The color of each expense category in the chart and the table. Unassigned expenses are a dark
 * neutral, so they stand apart from the categories.
 */
const CATEGORY_COLORS: Record<ExpenseCategory | typeof UNASSIGNED_ROW_ID, string> = {
    INFRASTRUCTURE: '#2563eb', // blue-600
    SERVICES_AND_LICENSES: '#f97316', // orange-500
    HARDWARE_AND_EQUIPMENT: '#10b981', // emerald-500
    VEHICLES: '#f59e0b', // amber-500
    TRAVEL: '#f472b6', // pink-400
    INSURANCE: '#15803d', // green-700
    CONSULTING_AND_ADMINISTRATION: '#7c3aed', // violet-600
    MEMBERSHIPS_AND_DONATIONS: '#ef4444', // red-500
    OTHER: '#94a3b8', // slate-400
    [UNASSIGNED_ROW_ID]: '#475569', // slate-600
};

/**
 * The expenses over twelve months: net expenses per month of service and expense category,
 * expandable to the suppliers, with the figures and the supplier ranking of the selected month.
 * Every amount links to the invoices it is made of.
 */
const AnalysisExpenseOverviewView: React.FC = () => {
    const intl = useIntl();
    const [isPending, startPendingTransition] = useTransition();
    const {range, selectedMonth, selectMonth, shiftRange, canShiftForward} = useAnalysisMonths();

    const data = useLazyLoadQuery<AnalysisExpenseOverviewViewQueryType>(
        graphql__45313cf56d4fd7a56a04,
        range,
        {fetchPolicy: 'store-and-network'}
    );
    const totals = data.expenseMonthlyTotals;

    const columns: PeriodColumn[] = totals.months.map(month => ({
        key: month.month,
        label: intl.formatDate(monthToDate(month.month), {month: 'short', year: '2-digit', timeZone: 'UTC'}),
        isProvisional: !month.isComplete,
    }));

    const categoryLabel = (category: string | null | undefined) => category
        ? intl.formatMessage({id: `expense.category.${category}`})
        : intl.formatMessage({id: 'view.analysisExpenseOverview.unassigned'});

    const categories = totals.categories.filter(category =>
        category.suppliers.length > 0 || category.amounts.some(amount => amount.netAmountInEuro !== 0)
    );

    const rows: PeriodRow[] = categories.map(category => {
        const id = category.expenseCategory ?? UNASSIGNED_ROW_ID;
        const label = categoryLabel(category.expenseCategory);
        return {
            id,
            label,
            sparklineLabel: label,
            marker: CATEGORY_COLORS[id as keyof typeof CATEGORY_COLORS],
            values: category.amounts.map(amount => ({amount: amount.netAmountInEuro, isProvisional: amount.isProvisional})),
            total: {amount: category.netAmountInEuro, isProvisional: category.amounts.some(amount => amount.isProvisional)},
            children: category.suppliers.map(supplier => ({
                id: supplier.supplierIdentifier,
                label: supplier.supplierName,
                sparklineLabel: supplier.supplierName,
                values: supplier.amounts.map(amount => ({amount: amount.netAmountInEuro, isProvisional: amount.isProvisional})),
                total: {amount: supplier.netAmountInEuro, isProvisional: supplier.amounts.some(amount => amount.isProvisional)},
            })),
        };
    });

    const totalRow: PeriodRow = {
        id: 'total',
        label: intl.formatMessage({id: 'view.analysisExpenseOverview.total'}),
        values: totals.months.map(month => ({amount: month.netAmountInEuro, isProvisional: month.isProvisional})),
    };

    // A supplier row only holds the invoices of its category, so its links keep the category filter.
    const rowFilter = (row: PeriodRow, parent?: PeriodRow) => parent
        ? {category: parent.id as ExpenseCategory | typeof NO_VALUE, supplier: row.id}
        : {category: row.id as ExpenseCategory | typeof NO_VALUE};

    const selectedIndex = totals.months.findIndex(month => month.month === selectedMonth);
    const selectedTotal = totals.months[selectedIndex]?.netAmountInEuro ?? 0;
    const previousTotal = selectedIndex > 0 ? totals.months[selectedIndex - 1].netAmountInEuro : null;

    // A supplier can bill for several categories, the ranking adds them up.
    const supplierAmounts = new Map<string, {name: string, amounts: number[]}>();
    categories.forEach(category => category.suppliers.forEach(supplier => {
        const entry = supplierAmounts.get(supplier.supplierIdentifier) ?? {name: supplier.supplierName, amounts: totals.months.map(() => 0)};
        supplier.amounts.forEach((amount, index) => entry.amounts[index] += amount.netAmountInEuro);
        supplierAmounts.set(supplier.supplierIdentifier, entry);
    }));
    const supplierRanking: RankingEntry[] = Array.from(supplierAmounts.entries()).map(([identifier, supplier]) => ({
        key: identifier,
        label: supplier.name,
        amount: supplier.amounts[selectedIndex] ?? 0,
        previousAmount: selectedIndex > 0 ? supplier.amounts[selectedIndex - 1] : null,
        href: expenseInvoicesPath({supplier: identifier, month: selectedMonth}),
    }));

    const select = (month: string) => startPendingTransition(() => selectMonth(month));
    const shift = (delta: number) => startPendingTransition(() => shiftRange(delta));

    return (
        <div className={`space-y-6 px-4 sm:px-6 lg:px-0 transition-opacity ${isPending ? 'opacity-60' : ''}`}>
            <MonthRangeHeader from={range.from} to={range.to} canShiftForward={canShiftForward} onShift={shift}/>

            <Hint>{intl.formatMessage({id: 'view.analysisExpenseOverview.notIncluded'})}</Hint>

            <MonthKpiCards
                totalLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.kpi.total'})}
                selectedMonth={selectedMonth}
                total={selectedTotal}
                previousTotal={previousTotal}
                averageTotals={totals.months.filter(month => month.isComplete).map(month => month.netAmountInEuro)}
                formatAmount={formatWholeAmount}
                increaseIsGood={false}
            >
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisExpenseOverview.kpi.toConfirm'})}
                    value={String(data.proposedServicePeriods.totalCount)}
                    sub={intl.formatMessage({id: 'view.analysisExpenseOverview.kpi.toConfirm.sub'})}
                    to={expenseInvoicesPath({status: 'PROPOSED'})}
                />
            </MonthKpiCards>

            <Panel>
                <StackedBarChart
                    columns={columns}
                    series={rows.map(row => ({id: row.id, label: String(row.label), color: row.marker ?? '#9ca3af', values: row.values.map(value => value.amount)}))}
                    formatAmount={formatWholeAmount}
                    totalLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.total'})}
                    provisionalLegendLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.legend.provisionalShort'})}
                    selectedColumnKey={selectedMonth}
                    onSelectColumn={select}
                />
            </Panel>

            <Panel fullWidthChildren>
                {rows.length === 0
                    ? <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.analysisExpenseOverview.empty'})}</p>
                    : <PeriodTable
                        columns={columns}
                        rows={rows}
                        totalRow={totalRow}
                        labelHeader={intl.formatMessage({id: 'view.analysisExpenseOverview.col.category'})}
                        totalHeader={intl.formatMessage({id: 'view.analysisExpenseOverview.col.total'})}
                        formatAmount={formatWholeAmount}
                        rowLink={(row, parent) => expenseInvoicesPath(rowFilter(row, parent))}
                        cellLink={(row, column, parent) => expenseInvoicesPath({...rowFilter(row, parent), month: column.key})}
                        selectedColumnKey={selectedMonth}
                        onSelectColumn={select}
                    />}
                <PeriodTableLegend
                    provisionalLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.legend.provisional'})}
                    note={intl.formatMessage({id: 'view.analysisExpenseOverview.legend.drillDown'})}
                />
            </Panel>

            <RankingList
                title={intl.formatMessage({id: 'view.analysisExpenseOverview.ranking'})}
                entries={supplierRanking}
                formatAmount={formatWholeAmount}
                increaseIsGood={false}
                emptyLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.ranking.empty'})}
                newLabel={intl.formatMessage({id: 'component.analysis.ranking.new'})}
                goneLabel={intl.formatMessage({id: 'view.analysisExpenseOverview.ranking.gone'})}
                barColor="#2563eb"
            />
        </div>
    );
};

export default AnalysisExpenseOverviewView;
